import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Weekly active-hours editor for a single gate or a bulk selection.
 *
 * The grid is one row per day of the week; each row toggles "active"
 * and exposes start + end time pickers. Multiple windows per day are
 * supported by clicking "+ עוד חלון" — useful for split shifts or
 * lunch breaks.
 *
 * Save is replace-all on both sides (settings toggle + schedule
 * windows), matching the server's PUT-as-replace semantics. In bulk
 * mode the same editor saves through the bulk endpoints and applies
 * to every gate in `gateIds`.
 */

const DAYS = [
  { value: 0, label: 'ראשון' },
  { value: 1, label: 'שני' },
  { value: 2, label: 'שלישי' },
  { value: 3, label: 'רביעי' },
  { value: 4, label: 'חמישי' },
  { value: 5, label: 'שישי' },
  { value: 6, label: 'שבת' },
];

const DEFAULT_WINDOW = { start: '06:00', end: '22:00' };


function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: '42px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        background: checked ? '#16a34a' : '#d1d5db',
        cursor: 'pointer',
        transition: 'background 0.15s',
        padding: 0,
      }}
    >
      <span style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '20px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.15s',
      }} />
    </button>
  );
}


// Re-shape the flat array of windows from the server into the grid
// the editor wants: { day: [{start, end}, ...] }.
function windowsToByDay(windows) {
  const out = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
  for (const w of (windows || [])) {
    if (out[w.day]) out[w.day].push({ start: w.start, end: w.end });
  }
  return out;
}

function byDayToWindows(byDay) {
  const out = [];
  for (const day of [0, 1, 2, 3, 4, 5, 6]) {
    for (const w of (byDay[day] || [])) {
      out.push({ day, start: w.start, end: w.end });
    }
  }
  return out;
}


function GateScheduleEditor({
  gateId = null,
  gateIds = null,
  groupId = null,
  apiBase = 'customer',
  initialAlertEnabled = false,
  onClose,
  onApplied,
}) {
  const isBulk = gateId == null && groupId == null && Array.isArray(gateIds) && gateIds.length > 0;
  const isGroup = groupId != null;
  const [byDay, setByDay] = useState(windowsToByDay([]));
  const [alertEnabled, setAlertEnabled] = useState(!!initialAlertEnabled);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (isBulk) {
      setByDay(windowsToByDay([]));
      setLoading(false);
      return;
    }
    setLoading(true);
    const url = isGroup
      ? `/api/${apiBase}/gate-groups/${groupId}/schedule`
      : `/api/${apiBase}/gates/${gateId}/schedule`;
    axios.get(url)
      .then(res => {
        if (cancelled) return;
        setByDay(windowsToByDay(res.data.windows));
      })
      .catch(() => {
        if (!cancelled) setError('שגיאה בטעינת לוח השעות');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [gateId, groupId, isBulk, isGroup, apiBase]);

  const setDayActive = (day, active) => {
    setByDay(prev => ({
      ...prev,
      [day]: active ? [DEFAULT_WINDOW] : [],
    }));
  };

  const updateWindow = (day, idx, patch) => {
    setByDay(prev => ({
      ...prev,
      [day]: prev[day].map((w, i) => i === idx ? { ...w, ...patch } : w),
    }));
  };

  const addWindow = (day) => {
    setByDay(prev => ({
      ...prev,
      [day]: [...(prev[day] || []), DEFAULT_WINDOW],
    }));
  };

  const removeWindow = (day, idx) => {
    setByDay(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== idx),
    }));
  };

  const copyToAllDays = () => {
    // Take whatever the first active day has and apply to every day.
    const firstActive = DAYS.find(d => (byDay[d.value] || []).length > 0);
    if (!firstActive) return;
    const template = byDay[firstActive.value];
    setByDay({
      0: [...template], 1: [...template], 2: [...template],
      3: [...template], 4: [...template], 5: [...template], 6: [...template],
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const windows = byDayToWindows(byDay);
    try {
      if (isGroup) {
        await axios.put(`/api/${apiBase}/gate-groups/${groupId}/schedule`, { windows });
        await axios.put(`/api/${apiBase}/gate-groups/${groupId}/settings`, {
          unauthorized_open_alert_enabled: alertEnabled,
        });
      } else if (isBulk) {
        await axios.put(`/api/${apiBase}/gates/schedule`, { gate_ids: gateIds, windows });
        await axios.put(`/api/${apiBase}/gates/settings`, {
          gate_ids: gateIds,
          settings: { unauthorized_open_alert_enabled: alertEnabled },
        });
      } else {
        await axios.put(`/api/${apiBase}/gates/${gateId}/schedule`, { windows });
        await axios.put(`/api/${apiBase}/gates/${gateId}/settings`, {
          unauthorized_open_alert_enabled: alertEnabled,
        });
      }
      onApplied && onApplied();
      onClose && onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בשמירה');
      setSaving(false);
    }
  };

  // Portal-mount the modal at document.body so a parent with a CSS
  // ``transform`` (e.g. the customer slide-panel) doesn't trap our
  // ``position: fixed`` overlay inside it.
  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 110,
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: '#fff',
        borderRadius: '10px',
        padding: '1.25rem',
        width: 'min(500px, calc(100vw - 24px))',
        maxHeight: '90vh',
        overflowY: 'auto',
        zIndex: 111,
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.05rem', fontWeight: 600 }}>
          שעות פעילות + התראת פתיחה לא מורשית
          {isBulk && <span style={{ color: '#6b7280', fontSize: '0.85rem', fontWeight: 400 }}> · {gateIds.length} שערים</span>}
        </h3>
        <p style={{ margin: 0, marginBottom: '1.25rem', fontSize: '0.8rem', color: '#6b7280' }}>
          הגדר שעות פעילות לכל יום בשבוע. שער שייפתח מחוץ לשעות שהגדרת — תישלח התראה. השאר ריק כדי שכל פתיחה תיחשב לא מורשית.
        </p>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.7rem 0.85rem',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          marginBottom: '1rem',
          background: '#f9fafb',
        }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
            התראת פתיחה לא מורשית: {alertEnabled ? 'פעילה' : 'כבויה'}
          </span>
          <ToggleSwitch checked={alertEnabled} onChange={setAlertEnabled} />
        </div>

        {loading ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', padding: '1rem 0' }}>טוען...</p>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              {DAYS.map(d => {
                const windows = byDay[d.value] || [];
                const active = windows.length > 0;
                return (
                  <div key={d.value} style={{
                    padding: '0.6rem 0',
                    borderBottom: '1px solid #f3f4f6',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={(e) => setDayActive(d.value, e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ width: '50px', fontWeight: 500, fontSize: '0.9rem' }}>{d.label}</span>
                      {!active && (
                        <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>לא פעיל</span>
                      )}
                      {active && windows[0] && (
                        <>
                          <input
                            type="time"
                            value={windows[0].start}
                            onChange={(e) => updateWindow(d.value, 0, { start: e.target.value })}
                            style={{ padding: '0.25rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                          <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>עד</span>
                          <input
                            type="time"
                            value={windows[0].end}
                            onChange={(e) => updateWindow(d.value, 0, { end: e.target.value })}
                            style={{ padding: '0.25rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                          <button
                            type="button"
                            onClick={() => addWindow(d.value)}
                            style={{
                              border: 'none', background: 'transparent', cursor: 'pointer',
                              fontSize: '0.78rem', color: '#3b82f6', padding: '0.2rem 0.4rem',
                            }}
                            title="הוסף חלון נוסף ביום זה (לדוגמה הפסקת צהריים)"
                          >
                            + עוד חלון
                          </button>
                        </>
                      )}
                    </div>
                    {/* Additional windows below the first */}
                    {active && windows.slice(1).map((w, idx) => (
                      <div key={idx + 1} style={{
                        display: 'flex', alignItems: 'center', gap: '0.65rem',
                        marginTop: '0.4rem', paddingRight: '85px',
                      }}>
                        <input
                          type="time"
                          value={w.start}
                          onChange={(e) => updateWindow(d.value, idx + 1, { start: e.target.value })}
                          style={{ padding: '0.25rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                        />
                        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>עד</span>
                        <input
                          type="time"
                          value={w.end}
                          onChange={(e) => updateWindow(d.value, idx + 1, { end: e.target.value })}
                          style={{ padding: '0.25rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                        />
                        <button
                          type="button"
                          onClick={() => removeWindow(d.value, idx + 1)}
                          style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: '0.78rem', color: '#dc2626', padding: '0.2rem 0.4rem',
                          }}
                        >
                          הסר
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={copyToAllDays}
              style={{
                padding: '0.45rem 0.9rem',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                borderRadius: '5px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                marginBottom: '1rem',
              }}
            >
              העתק לכל ימי השבוע
            </button>
          </>
        )}

        {error && (
          <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '0.45rem 0.9rem',
              border: 'none',
              background: 'transparent',
              color: '#6b7280',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            ביטול
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            style={{
              padding: '0.45rem 1.1rem',
              border: 'none',
              background: '#1f2937',
              color: '#fff',
              borderRadius: '5px',
              fontSize: '0.85rem',
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'שומר...' : (isBulk ? 'החל על כולם' : 'שמור')}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

export default GateScheduleEditor;
